import React, { useState, useEffect } from 'react';
import { updateSettings } from '../../services/settingsService';
import { SuccessCircle, WarningIcon } from '../../assets/icons';
import Spinner from '../common/Spinner';
import useModalClose from '../../hooks/useModalClose';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
const STEPS = {
  FORM: 0,
  CONFIRM: 1,
  COMPLETE: 2
};

const EditSettingsModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    context: '',
 
  });
  const [updatedUser, setUpdatedUser] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        context: user.context,
      });
    }
  }, [user]);

  const resetState = () => {
    setCurrentStep(STEPS.FORM);
    setIsLoading(false);
    setError(null);
    setUpdatedUser(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;     
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    
  };

  const validateForm = () => {
    if (!formData.context) {
      setError('Lütfen tüm alanları doldurunuz.');
      return false;
    }    
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    if (validateForm()) {
      setCurrentStep(STEPS.CONFIRM);
    }
  };

  const handleBack = () => {
    setCurrentStep(STEPS.FORM);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    setCurrentStep(STEPS.COMPLETE);

    try {
      await updateSettings(user.id, {
        ...formData,
        isActive: true
      });
      const updatedUserData = {
        ...user,
        ...formData
      };
      setUpdatedUser(updatedUserData);
      onSuccess(updatedUserData);
    } catch (error) {
      setError('Ayar güncellenirken bir hata oluştu.');
      console.error('Update user error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="stepper">
      <div className={`step ${currentStep >= STEPS.FORM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.FORM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Ayar Bilgisi</span>
      </div>
      <div className={`step ${currentStep >= STEPS.CONFIRM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.CONFIRM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Bilgi Onayı</span>
      </div>
      <div className={`step ${currentStep >= STEPS.COMPLETE ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.COMPLETE && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Tamamlandı</span>
      </div>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="create-user-form">
      <div className="form-group">
        <label htmlFor="context">Ayar değeri</label>
        {user?.id === 1 ? (
          <div>
            <CKEditor
              editor={ClassicEditor}
              data={formData.context}
              onChange={(_, editor) => {
                const content = editor.getData();
                // Remove HTML tags to count plain text characters
                const plainText = content.replace(/<[^>]+>/g, '');
                if (plainText.length <= 4000) {
                  setFormData((prev) => ({
                    ...prev,
                    context: content
                  }));
                  setError(null); // Clear error if within limit
                } else {
                  // Truncate to 300 characters
                  const truncatedText = plainText.slice(0, 4000);
                  // Rebuild HTML content (simplified; adjust based on your needs)
                  editor.setData(truncatedText);
                  setFormData((prev) => ({
                    ...prev,
                    context: truncatedText
                  }));
                  setError('Maksimum 4000 karakter girebilirsiniz.');
                }
              }}
            />
            <div className="char-counter text-sm text-gray-500 mt-1">
              {formData.context.replace(/<[^>]+>/g, '').length}/4000 karakter
            </div>
          </div>
        ) : (
          <input
            maxLength={3000}
            type="text"
            id="context"
            name="context"
            value={formData.context}
            onChange={handleInputChange}
            placeholder="Değer"
          />
        )}
      </div>
  
      {error && <div className="error-message">{error}</div>}
      <div className="form-actions">
        <button type="submit" className="submit-button">
          Düzenlemeyi Kaydet
        </button>
      </div>
    </form>
  );
  const renderConfirmation = () => (
    <div className="confirmation-step">
      <WarningIcon />
      <h3>Ayarı güncellemek üzeresiniz. Onaylıyor musunuz?</h3>
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Yeni ayar değeri</th>             
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{`${formData.context}`}</td>
              
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button type="button" onClick={handleBack} className="back-button">
          İptal Et
        </button>
        <button type="button" onClick={handleConfirm} className="confirm-button">
          Sonraki Adım
        </button>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="complete-step">
      {isLoading ? (
        <div className="loading-state">
          <Spinner size="large" />
          <h3>Süreciniz tamamlanıyor...</h3>
          <button type="button" onClick={handleClose} className="cancel-button">
            Süreci İptal Et
          </button>
        </div>
      ) : updatedUser ? (
        <div className="success-state">
          <SuccessCircle />
          <h3>Süreciniz tamamlandı. Ayar değeri güncellendi.</h3>
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Ayar Değeri</th>
                 
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{`${updatedUser.context}`}</td>
                 
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" onClick={handleClose} className="close-button">
            Kapat
          </button>
        </div>
      ) : (
        <div className="error-state">
          <div className="error-icon">✕</div>
          <h3>Hata Oluştu</h3>
          <p>{error}</p>
          <button type="button" onClick={handleBack} className="retry-button">
            Tekrar Dene
          </button>
        </div>
      )}
    </div>
  );

  // Add modal closing behaviors
  useModalClose(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Bilgilerini Düzenle</h3>
        </div>
        {renderStepIndicator()}
        <div className="modal-body">
          {currentStep === STEPS.FORM && renderForm()}
          {currentStep === STEPS.CONFIRM && renderConfirmation()}
          {currentStep === STEPS.COMPLETE && renderComplete()}
        </div>
      </div>
    </div>
  );
};

export default EditSettingsModal; 